import { useDisclosure } from "@caspeco/casper-ui-library";
import { useFilteredExperiences } from "api/api-hooks/use-available-times";
import { useCancelBooking } from "api/api-hooks/use-cancel-booking";
import { useExperienceView } from "api/api-hooks/use-experience-view";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { BookableItem } from "components/booking-modal/bookable-item";
import { BookingModal } from "components/booking-modal/booking-modal";
import ConfirmationDialog from "components/confirm-dialog";
import { findExperienceChains } from "components/experiences/find-experience-chains";
import { useLocalizedValue } from "helpers/use-localized-value";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import { TrackingStepName } from "logger/user-tracking";
import queryClient from "query-client";
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Routes } from "routes";
import { UrlParamName } from "search-params";
import { Time } from "store/store-types";
import { AvailableTimesSection } from "types/available-times";
import { Experience } from "types/experience";
import { TimeSlot } from "types/time-slot";
import { WebRule } from "types/web-rule";
import { findFamilyVariant } from "utils/experience-family";
import { buildExperienceSuggestions } from "utils/experience-suggestions";

interface ExperienceModalProps {
    experience: Experience | null;
    onClose: () => void;
    onConfirm?: () => void;
    openOnMenuStep?: boolean;
    /** Opens another experience in place. Without it no alternatives are offered. */
    onSelectExperience?: (experienceId: number) => void;
    /** Returns to the experience a suggestion was followed from, so it is not a one-way door. */
    onReturnToOrigin?: () => void;
}

function ExperienceModal({
    experience,
    onClose,
    onConfirm,
    openOnMenuStep,
    onSelectExperience,
    onReturnToOrigin,
}: ExperienceModalProps) {
    const { t } = useTranslation();
    const { bookingId } = useParams();
    const state = useBookingStateContext();
    const { date, guests, isBookingRequest } = state;
    const { handleCreateBooking } = useBookingActionsContext();
    const navigate = useNavigate();
    const location = useLocation();
    const { data: booking } = useWebBooking();
    const cancelBooking = useCancelBooking();
    const filteredExperiences = useFilteredExperiences(date);
    // Only suggestion-capable callers need the catalogue. Without onSelectExperience there are no
    // suggestions to build, and fetching it would cost the details step a round trip for nothing.
    const { data: experienceViewItems } = useExperienceView(!!onSelectExperience);
    const activeExperience =
        date && experience ? findFamilyVariant(filteredExperiences, experience.id) : experience;

    const localizedName = useLocalizedValue(experience?.name);
    const localizedDescription = useLocalizedValue(experience?.description);

    const {
        isOpen: isConfirmDialogOpen,
        onClose: onConfirmDialogClose,
        onOpen: onConfirmDialogOpen,
    } = useDisclosure();

    const pendingWaitlistRef = useRef<{ times: TimeSlot[]; close: () => void } | null>(null);

    const proceedWithWaitlist = (times: TimeSlot[], close: () => void) => {
        const stateWithTimes = { ...state, times: times as Time[], isWaitList: true };
        handleCreateBooking?.(stateWithTimes);
        onConfirm?.();
        close();
    };

    const handleWaitlistContinue = (times: TimeSlot[], close: () => void) => {
        if (booking?.articles && booking.articles.length > 0) {
            pendingWaitlistRef.current = { times, close };
            onConfirmDialogOpen();
            return;
        }
        proceedWithWaitlist(times, close);
    };

    const handleCancelBookingAndWaitlist = async () => {
        const pending = pendingWaitlistRef.current;
        if (!pending) return;
        await cancelBooking.mutateAsync({ systemTriggered: true });
        queryClient.removeQueries({ queryKey: ["booking", bookingId] });
        await queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
        proceedWithWaitlist(pending.times, pending.close);
        pendingWaitlistRef.current = null;
    };

    const bookedSectionIds = useMemo(
        () => new Set(booking?.articles?.map((article) => article.sectionId) ?? []),
        [booking?.articles]
    );

    const suggestions = useMemo(
        () =>
            onSelectExperience
                ? buildExperienceSuggestions({
                      experiences: filteredExperiences,
                      items: experienceViewItems ?? [],
                      current: activeExperience,
                      guests,
                      bookedSectionIds,
                      isBookingRequest: !!isBookingRequest,
                  })
                : [],
        [
            onSelectExperience,
            filteredExperiences,
            experienceViewItems,
            activeExperience,
            guests,
            bookedSectionIds,
            isBookingRequest,
        ]
    );

    /**
     * Takes the guest to the request flow for this experience, keeping the guests and date they
     * already chose. Reached when they picked those before opening the experience, so the filter
     * modal's own redirect never ran (BOOK-3911).
     */
    const goToBookingRequest = useCallback(
        (experienceId: number) => {
            const requestPath = location.pathname.replace(
                Routes.EXPERIENCE_VIEW,
                Routes.BOOKING_REQUEST
            );
            const params = new URLSearchParams(location.search);
            params.set(UrlParamName.EXPERIENCE_ID, String(experienceId));
            // Reached only once a date is chosen, so the wizard's date step would ask for one the
            // guest already gave. Straight to picking a time instead.
            const pathname = date ? `${requestPath}/${Routes.AVAILABILITY}` : requestPath;
            onClose();
            void navigate({ pathname, search: params.toString() });
        },
        [date, location.pathname, location.search, navigate, onClose]
    );

    const item = useMemo<BookableItem | null>(() => {
        if (!activeExperience) return null;

        const hasMenuChoice = activeExperience.activities.some((activity) =>
            activity.timeSets.some((timeSet) => timeSet.menuGuid !== "")
        );
        const isSingleActivity = activeExperience.activities.length === 1;
        // A request-only experience is judged on the same footing as the request flow: its slots
        // carry no seats to check a party size against (BOOK-3911).
        const asRequest = !!isBookingRequest || activeExperience.bookingRequestOnly;
        const { startTimes, chains } = findExperienceChains(activeExperience, guests, asRequest);
        const displaySections = activeExperience.activities.map((activity, index) => {
            const isFirstIndex = index === 0;
            // Its rules are generated text rows; the experience's own description is the message.
            const timeSets = activeExperience.informationOnly ? [] : activity.timeSets;

            return new AvailableTimesSection({
                id: activity.sectionId,
                timeSets: timeSets.map(
                    (timeSet) =>
                        new WebRule({
                            ...timeSet,
                            id: timeSet.id,
                            times:
                                isFirstIndex && !isSingleActivity
                                    ? timeSet.times.filter((time) => startTimes.includes(time))
                                    : timeSet.times,
                        })
                ),
            });
        });

        return {
            id: activeExperience.id,
            name: localizedName,
            description: localizedDescription,
            image: activeExperience.imageReference16x9,
            activities: displaySections,
            chains,
            maximumGapTime: activeExperience.maximumGapTime,
            // Inquiry rules carry no waitlist, so a request-only experience has none to offer.
            supportsWaitlist:
                isSingleActivity &&
                !activeExperience.bookingRequestOnly &&
                !activeExperience.informationOnly,
            isRequestOnly: activeExperience.bookingRequestOnly,
            isInformationOnly: activeExperience.informationOnly,
            // Inside the request flow the regular continue already makes the request; handing off
            // again would only nest another availability step under the current one.
            onRequestContinue:
                activeExperience.bookingRequestOnly && !isBookingRequest
                    ? () => goToBookingRequest(activeExperience.id)
                    : undefined,
            onAfterConfirm: onConfirm,
            continueTrackingId: "continue-experience-selection",
            stepName: TrackingStepName.ExperienceModal,
            trackingMetadata: { experienceName: localizedName || undefined },
            trackingDependencies: [experience?.id, isBookingRequest],
            filterExperienceId: experience?.id,
            filterButtonTrackingId: "open-guest-date-filter-experience",
            showStartTimeHeading: activeExperience.activities.length > 1,
            hasMenuChoice,
            // Nothing to badge: a request-only experience has no seats that could run out.
            showAvailabilityBadge:
                isSingleActivity &&
                !activeExperience.bookingRequestOnly &&
                !activeExperience.informationOnly,
            showSelectionSearch: true,
            suggestions,
            onSelectSuggestion: onSelectExperience,
            onReturnToOrigin,
        };
    }, [
        activeExperience,
        localizedName,
        localizedDescription,
        onConfirm,
        experience?.id,
        guests,
        isBookingRequest,
        suggestions,
        onSelectExperience,
        onReturnToOrigin,
        goToBookingRequest,
    ]);

    return (
        <>
            <BookingModal
                item={item}
                isOpen={!!experience}
                onClose={onClose}
                onWaitlistContinue={item?.supportsWaitlist ? handleWaitlistContinue : undefined}
                openOnMenuStep={openOnMenuStep}
            />
            <ConfirmationDialog
                secondaryButtonText={t("summary.cancel")}
                primaryButtonText={t("waitList.continue")}
                isOpen={isConfirmDialogOpen}
                title={t("waitList.bookingClearWarningTitle")}
                message={t("waitList.bookingClearWarningMessage")}
                onClose={onConfirmDialogClose}
                secondaryAction={onConfirmDialogClose}
                primaryAction={handleCancelBookingAndWaitlist}
            />
        </>
    );
}

export { ExperienceModal };
